import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import ProfileInfo from "./components/ProfileInfo";
import FavoriteProducts from "./components/FavoriteProducts";
import PriceAlerts from "./components/PriceAlerts";

export default function ProfilePage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false); // Admin kontrolü için
  const [activeTab, setActiveTab] = useState<
    "profile" | "favorites" | "alerts"
  >("profile");

  // Ürün Ekleme Form State'leri
  const [productData, setProductData] = useState({
    title: "",
    current_price: "",
    image_url: "",
    category: "",
    discount_rate: "",
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const {
        data: { user: authUser },
        error,
      } = await supabase.auth.getUser();

      if (error || !authUser) {
        navigate("/login");
        return;
      }

      setUser(authUser);

      // ADMİN KONTROLÜ: Veritabanındaki user_roles veya admin emaili
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", authUser.id)
        .maybeSingle();

      if (roleData?.role === "admin" || authUser.email === "admin@outletlio.com") {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error("Kullanıcı kontrolü hatası:", error);
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  // ÜRÜN EKLEME FONKSİYONU
  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await supabase.from("products").insert([productData]);
    if (error) {
      alert("Hata: Ürün eklenemedi!");
    } else {
      alert("Ürün Başarıyla Eklendi!");
      setProductData({
        title: "",
        current_price: "",
        image_url: "",
        category: "",
        discount_rate: "",
      });
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Çıkış hatası:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar isScrolled={isScrolled} />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Profilim
                </h1>
                <p className="text-gray-600">Hesap bilgilerinizi yönetin</p>
              </div>
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <button
                    onClick={() => navigate("/admin")}
                    className="px-6 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-rose-600 transition-all cursor-pointer flex items-center space-x-2 shadow-md"
                  >
                    <i className="ri-dashboard-line text-lg"></i>
                    <span>Admin Paneli</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="px-6 py-3 bg-white border-2 border-gray-200 text-gray-900 rounded-xl font-semibold hover:border-rose-500 hover:text-rose-500 transition-all cursor-pointer flex items-center space-x-2"
                >
                  <i className="ri-logout-box-line"></i>
                  <span>Çıkış Yap</span>
                </button>
              </div>
            </div>
          </div>

          {/* Sekme Menüsü */}
          <div className="flex items-center space-x-2 mb-8 border-b border-gray-200">
            {["profile", "favorites", "alerts"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`px-6 py-3 font-medium transition-all cursor-pointer border-b-2 ${
                  activeTab === tab
                    ? "border-rose-500 text-rose-600"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab === "profile" && (
                  <>
                    <i className="ri-user-line mr-2"></i>Profil
                  </>
                )}
                {tab === "favorites" && (
                  <>
                    <i className="ri-heart-line mr-2"></i>Favoriler
                  </>
                )}
                {tab === "alerts" && (
                  <>
                    <i className="ri-notification-line mr-2"></i>Alarmlar
                  </>
                )}
              </button>
            ))}
          </div>

          {/* Dinamik İçerik Alanı */}
          <div className="fade-in mb-12">
            {activeTab === "profile" && <ProfileInfo user={user} />}
            {activeTab === "favorites" && <FavoriteProducts />}
            {activeTab === "alerts" && <PriceAlerts />}
          </div>

          {/* ADMİN PANELİ: Sadece admin giriş yaptığında en altta görünür */}
          {isAdmin && (
            <div className="mt-12 bg-white p-8 rounded-2xl shadow-sm border-2 border-rose-50 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-2 h-6 bg-rose-500 rounded-full"></span>
                <h2 className="text-xl font-black text-gray-900 italic tracking-tighter uppercase">
                  Admin Panel - Ürün Ekle
                </h2>
              </div>

              <form
                onSubmit={handleAddProduct}
                className="grid grid-cols-1 md:grid-cols-2 gap-5"
              >
                <input
                  type="text"
                  placeholder="Ürün Başlığı"
                  className="p-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500"
                  value={productData.title}
                  onChange={(e) =>
                    setProductData({ ...productData, title: e.target.value })
                  }
                  required
                />
                <input
                  type="number"
                  placeholder="Fiyat (TL)"
                  className="p-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500"
                  value={productData.current_price}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      current_price: e.target.value,
                    })
                  }
                  required
                />
                <input
                  type="text"
                  placeholder="Görsel URL"
                  className="p-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500"
                  value={productData.image_url}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      image_url: e.target.value,
                    })
                  }
                  required
                />
                <input
                  type="text"
                  placeholder="Kategori"
                  className="p-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500"
                  value={productData.category}
                  onChange={(e) =>
                    setProductData({ ...productData, category: e.target.value })
                  }
                  required
                />
                <input
                  type="number"
                  placeholder="İndirim Oranı (%)"
                  className="p-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-rose-500 md:col-span-2"
                  value={productData.discount_rate}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      discount_rate: e.target.value,
                    })
                  }
                />

                <button
                  type="submit"
                  className="md:col-span-2 py-4 bg-gray-900 text-white rounded-xl font-black hover:bg-rose-600 transition-all uppercase italic tracking-widest text-sm shadow-lg"
                >
                  Ürünü Hemen Listele
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
